import { useAppInstance } from "./useAppInstance"
import { useAppTemplate } from "./useAppTemplate"
import { FetchState, hasValue } from "../Models/Client/FetchState"
import {
    MetricRequest,
    getOneQualityName,
    getQualityDisplayName
} from "../Models/Client/MetricRequest"
import { useMetricVariants } from "./useMetricVariants"
import { Result } from "../Models/Utility/Result"
import {
    isMetricsConfigured,
    isNonEmptyMetric
} from "../Models/API/AppInstanceModel"
import { useState, useMemo, useEffect } from "react"

interface MetricSelection {
    chosenVariantFetch: FetchState<MetricRequest | undefined>
    qualityName: string | undefined
    /**
     * mappings between quality name and its display name
     */
    qualityNames: { [name: string]: string }
    setQualityName: (value: string) => void
}

export function useMetricSelection(appID?: string): MetricSelection {
    const templateFetch = useAppTemplate(appID)
    const instanceFetch = useAppInstance(appID)
    const variantsFetch = useMetricVariants(appID)
    const chosenVariantFetch = !hasValue(templateFetch)
        ? templateFetch
        : !hasValue(instanceFetch)
        ? instanceFetch
        : Result.ok(
              isMetricsConfigured(instanceFetch.value, templateFetch.value) &&
                  isNonEmptyMetric(instanceFetch.value.appMetrics.metric)
                  ? instanceFetch.value.appMetrics.metric
                  : undefined
          )
    const [qualityName, setQualityName] =
        useState<string | undefined>(undefined)
    const qualityNames = useMemo(() => {
        if (!hasValue(variantsFetch) || variantsFetch.value.length === 0) {
            return []
        }

        const variant = variantsFetch.value[0]

        return Object.keys(variant.quality).reduce(
            (passed, name) => ({
                ...passed,
                [name]: getQualityDisplayName(variant, name)
            }),
            {}
        )
    }, [variantsFetch])

    // initialize quality name selection
    useEffect(() => {
        if (qualityName !== undefined || !hasValue(chosenVariantFetch)) {
            return
        }

        // if metrics is already selected, set to selected variant's quality
        if (chosenVariantFetch.value !== undefined) {
            setQualityName(getOneQualityName(chosenVariantFetch.value))
            return
        }

        // otherwise, select one of available quality names
        setQualityName(Object.keys(qualityNames)[0])
    }, [qualityNames, qualityName, chosenVariantFetch])

    return useMemo(
        () => ({
            chosenVariantFetch,
            qualityName,
            qualityNames,
            setQualityName
        }),
        [chosenVariantFetch, qualityName, qualityNames]
    )
}
